import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Gauge, Package, UsersThree, Wrench, Barcode, SignOut, List, X
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      {/* Sidebar */}
      <aside
        className={`fixed md:sticky top-0 left-0 h-screen w-64 bg-zinc-950 border-r border-zinc-900 flex flex-col z-40 transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="px-5 py-6 border-b border-zinc-900">
          <Logo size={36} />
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          <div className="px-3 pb-3 text-[10px] tracking-[0.25em] uppercase text-zinc-500">Operations</div>
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-zinc-900 text-[#F5A623] border border-zinc-800"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900/70"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <>
              <div className="px-3 pt-6 pb-3 text-[10px] tracking-[0.25em] uppercase text-zinc-500">Admin</div>
              <NavLink
                to="/users"
                data-testid="nav-users"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-zinc-900 text-[#F5A623] border border-zinc-800"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-900/70"
                  }`
                }
              >
                <UsersThree size={18} weight="duotone" />
                <span>Users</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-900">
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-zinc-900/50">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#F5A623] to-[#E67E22] grid place-items-center text-zinc-950 text-sm font-bold">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-100 truncate">{user?.name}</div>
              <div className="text-[10px] tracking-wider uppercase text-zinc-500">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              data-testid="logout-button"
              className="p-2 rounded-md text-zinc-400 hover:text-[#F5A623] hover:bg-zinc-900 transition-colors"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-14 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 no-print">
          <button
            className="md:hidden p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-nav-toggle"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
          <div className="hidden md:block text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Kerma Games · Internal Operations
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            {new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
